'use client';

import React, { useEffect, useState } from 'react';
import { Maximize2, Minus, Move, Plus, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useBracketPan } from '@/hooks/useBracketPan';

interface BracketPanCanvasProps {
  children: React.ReactNode;
  className?: string;
  height?: number | string;
}

export const BracketPanCanvas: React.FC<BracketPanCanvasProps> = ({
  children,
  className,
  height = 600,
}) => {
  const {
    containerRef,
    contentRef,
    innerStyle,
    scale,
    isDragging,
    handlers,
    zoomIn,
    zoomOut,
    reset,
    fit,
  } = useBracketPan();
  const [showHint, setShowHint] = useState(true);

  useEffect(() => {
    const timer = window.setTimeout(() => setShowHint(false), 4000);
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <div className={cn('relative', className)}>
      <div className="pointer-events-none absolute right-3 top-3 z-20 flex items-center gap-1.5">
        <div className="rounded-md border border-border/60 bg-background/90 px-2 py-1 text-xs font-medium tabular-nums text-muted-foreground shadow-sm backdrop-blur-sm">
          {Math.round(scale * 100)}%
        </div>
        <div className="pointer-events-auto flex items-center gap-1 rounded-lg border border-border/60 bg-background/90 p-1 shadow-sm backdrop-blur-sm">
          <ControlButton label="Alejar" onClick={zoomOut}>
            <Minus className="h-3.5 w-3.5" />
          </ControlButton>
          <ControlButton label="Acercar" onClick={zoomIn}>
            <Plus className="h-3.5 w-3.5" />
          </ControlButton>
          <ControlButton label="Ajustar a pantalla" onClick={fit}>
            <Maximize2 className="h-3.5 w-3.5" />
          </ControlButton>
          <ControlButton label="Restablecer vista" onClick={reset}>
            <RotateCcw className="h-3.5 w-3.5" />
          </ControlButton>
        </div>
      </div>

      {showHint && (
        <div className="pointer-events-none absolute bottom-4 left-1/2 z-20 -translate-x-1/2 animate-in fade-in slide-in-from-bottom-2 duration-300">
          <div className="flex items-center gap-2 rounded-full border border-border/60 bg-background/90 px-4 py-2 text-xs text-muted-foreground shadow-sm backdrop-blur-sm">
            <Move className="h-3.5 w-3.5 shrink-0 text-primary" />
            <span>Arrastra o usa scroll para navegar · Ctrl + scroll para zoom</span>
          </div>
        </div>
      )}

      <div
        ref={containerRef}
        className={cn(
          'overflow-hidden rounded-xl border border-border/30 bg-muted/5 touch-none select-none',
          isDragging ? 'cursor-grabbing' : 'cursor-grab',
        )}
        style={{ height }}
        {...handlers}
      >
        <div ref={contentRef} className="inline-block w-max" style={innerStyle}>
          {children}
        </div>
      </div>
    </div>
  );
};

interface ControlButtonProps {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}

const ControlButton: React.FC<ControlButtonProps> = ({ label, onClick, children }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-7 w-7"
        onClick={onClick}
        aria-label={label}
      >
        {children}
      </Button>
    </TooltipTrigger>
    <TooltipContent side="bottom">{label}</TooltipContent>
  </Tooltip>
);

export default BracketPanCanvas;
